import { useCallback, useState, useEffect } from 'react';
import { useTranslation as useReactI18nTranslation } from 'react-i18next';

import i18n from 'lib/i18n';
import isBrowser from 'lib/isBrowser';

// 번역 옵션 타입 정의
interface TranslationOptions {
  defaultValue?: string;
  count?: number;

  [key: string]: unknown; // 동적 변수를 위한 인덱스 시그니처
}

/**
 * 번역을 위한 커스텀 훅
 * react-i18next의 useTranslation 훅을 래핑하여 사용합니다.
 */
const useTranslation = () => {
  const { t: originalT } = useReactI18nTranslation();
  // 서버와 클라이언트 간의 일관성을 위해 초기값은 항상 'en'으로 설정
  const [ language, setLanguage ] = useState('en');
  const [ isClient, setIsClient ] = useState(false);

  // 클라이언트 사이드에서만 실행되는 코드
  useEffect(() => {
    if (isBrowser()) {
      setIsClient(true);
      // 클라이언트에서만 localStorage에서 언어 설정을 가져와 적용
      const storedLanguage = localStorage.getItem('i18nextLng');
      if (storedLanguage) {
        setLanguage(storedLanguage);
      } else {
        setLanguage(i18n.language);
      }
    }
  }, []);

  // i18n 언어 변경 감지
  useEffect(() => {
    if (!isClient) return;

    const handleLanguageChanged = (lng: string) => {
      setLanguage(lng);
    };

    // 언어 변경 이벤트 리스너 등록
    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      // 컴포넌트 언마운트 시 이벤트 리스너 제거
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [ isClient ]);

  // 서버 사이드 렌더링과 클라이언트 사이드 렌더링 간의 일관성을 유지하기 위한 t 함수
  const t = useCallback((key: string, options?: TranslationOptions) => {
    // 서버 사이드 렌더링 시에는 키 자체를 반환하지 않고 기본 번역을 사용
    if (!isClient) {
      // options에 defaultValue가 있으면 그것을 사용
      if (options?.defaultValue) {
        return options.defaultValue;
      }

      // 영어 번역 파일에서 기본값 찾기
      try {
        const parts = key.split('.');
        let translation = i18n.getResource('en', 'translation', parts[0]);

        if (parts.length === 1) {
          return typeof translation === 'string' ? translation : key;
        }

        for (let i = 1; i < parts.length; i++) {
          if (translation && typeof translation === 'object' && parts[i] in translation) {
            translation = translation[parts[i]];
          } else {
            return key; // 번역을 찾을 수 없으면 키 자체를 반환
          }
        }

        return typeof translation === 'string' ? translation : key;
      } catch (error) {
        return key;
      }
    }

    return originalT(key, options);
  }, [ originalT, isClient ]);

  const changeLanguage = useCallback((lng: string) => {
    if (!isClient) return;

    if (i18n.changeLanguage) {
      i18n.changeLanguage(lng).then(() => {
        localStorage.setItem('i18nextLng', lng);
        setLanguage(lng);
      });
    }
  }, [ isClient ]);

  return {
    t,
    changeLanguage,
    currentLanguage: language, // i18n.language 대신 state 사용
  };
};

export default useTranslation;
